import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const WORKFLOW_API_URL = `${BASE_API_URL}/QmsMyTask`;

export interface ApprovalTask {
  id: string;
  documentId: string;
  documentNumber: string;
  documentTitle: string;
  documentCategory: string;
  currentVersion: string;
  submittedBy: string;
  submittedDate: string;
  daysWaiting?: number;
  priority: 'high' | 'medium' | 'low';
  currentStep: string;
  previousApprovers: string[];
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedDate?: string;
  approvalComment?: string;
  approvedBy?: string;
}

export interface ApprovalActionRequest {
  action: 'approve' | 'reject';
  comment?: string;
}

/**
 * Get pending approval tasks for current user
 * Calls the GET /api/QmsWorkflowInstance/pending?userId={userId} endpoint
 */
export const getPendingTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/pending`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data || [];
  } catch (error: any) {
    console.error('Failed to get pending tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get pending tasks'
    );
  }
};

/**
 * Get approved tasks history for current user
 * Calls the GET /api/QmsWorkflowInstance/approved?userId={userId} endpoint
 */
export const getApprovedTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/approved`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data || [];
  } catch (error: any) {
    console.error('Failed to get approved tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get approved tasks'
    );
  }
};

/**
 * Get rejected tasks history for current user
 * Calls the GET /api/QmsWorkflowInstance/rejected?userId={userId} endpoint
 */
export const getRejectedTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/rejected`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data || [];
  } catch (error: any) {
    console.error('Failed to get rejected tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get rejected tasks'
    );
  }
};

/**
 * Approve a workflow task
 * Calls the POST /api/QmsWorkflowInstance/{instanceId}/approve endpoint
 */
export const approveTask = async (instanceId: string, comment?: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    const response = await axios.post(
      `${WORKFLOW_API_URL}/${instanceId}/approve`,
      {
        comment: comment || '',
        userId: userId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to approve task:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to approve task'
    );
  }
};

/**
 * Reject a workflow task
 * Calls the POST /api/QmsWorkflowInstance/{instanceId}/reject endpoint
 */
export const rejectTask = async (instanceId: string, comment: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    const response = await axios.post(
      `${WORKFLOW_API_URL}/${instanceId}/reject`,
      {
        comment: comment,
        userId: userId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to reject task:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to reject task'
    );
  }
};

/**
 * Get workflow instance details
 * Calls the GET /api/QmsWorkflowInstance/{instanceId} endpoint
 */
export const getWorkflowInstance = async (instanceId: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${WORKFLOW_API_URL}/${instanceId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to get workflow instance:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get workflow instance'
    );
  }
};
